'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { IscaChat } from '@/components/jhow/isca-chat'
import { SiteFooter } from '@/components/ui/site-footer'
import { UserMenu } from '@/components/ui/user-menu'

/* ============================================================================
   HOME eBuz — proposta MANIFESTO
   Clareza estratégica -> Manifesto -> Implantação.
   A oferta de entrada (R$ 19,90) saiu desta página e vive em /passaporte.
   ============================================================================ */

const NAV_LINKS = [
  { label: 'O projeto', href: '#proposta' },
  { label: 'O que você recebe', href: '#entrega' },
  { label: 'A jornada', href: '#jornada' },
  { label: 'Para quem é', href: '#para-quem' },
  { label: 'Dúvidas', href: '#faq' },
]

const WHATSAPP_NUMBER = '5561999969213'
const FUNILCHATBOT_WEBHOOK = 'https://api.funilchatbot.com.br/wh/JvVtgM2KtxAVrgl-'

const DDI_OPTIONS = [
  { code: '+55', label: 'BR +55' },
  { code: '+1', label: 'US +1' },
  { code: '+351', label: 'PT +351' },
  { code: '+34', label: 'ES +34' },
  { code: '+44', label: 'UK +44' },
  { code: '+33', label: 'FR +33' },
  { code: '+49', label: 'DE +49' },
  { code: '+39', label: 'IT +39' },
  { code: '+81', label: 'JP +81' },
  { code: '+54', label: 'AR +54' },
  { code: '+56', label: 'CL +56' },
  { code: '+57', label: 'CO +57' },
  { code: '+52', label: 'MX +52' },
]

const MOMENTOS = [
  'Tenho experiência, mas nada estruturado ainda',
  'Já atendo clientes e quero transformar isso em projeto',
  'Já tenho curso, mentoria ou produto rodando',
  'Já tenho método ou livro começado',
  'Outro momento',
]

const WA_ICON = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

/* ====== Ícones ====== */
function TargetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <path strokeLinecap="round" d="M15.5 8.5 20.5 3.5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 3.2h2.3v2.3" />
    </svg>
  )
}
function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
      {/* livro aberto: duas paginas em V, com raios saindo de cima */}
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.1 10.4C10.1 9.4 8.3 8.9 5.9 8.9H2.7v9.4h3.2c2.4 0 4.2.5 5.2 1.5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12.9 10.4c1-1 2.8-1.5 5.2-1.5h3.2v9.4h-3.2c-2.4 0-4.2.5-5.2 1.5Z" />
      <path strokeLinecap="round" d="M12 6.3V3.9M7.9 7.4 6.6 5.6M16.1 7.4l1.3-1.8" />
    </svg>
  )
}
function GearIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.3 3.3a1 1 0 0 1 1-.8h1.4a1 1 0 0 1 1 .8l.3 1.6c.5.2 1 .5 1.4.8l1.5-.6a1 1 0 0 1 1.2.4l.7 1.2a1 1 0 0 1-.2 1.3l-1.2 1c0 .3.1.6.1.9s0 .6-.1.9l1.2 1a1 1 0 0 1 .2 1.3l-.7 1.2a1 1 0 0 1-1.2.4l-1.5-.6c-.4.3-.9.6-1.4.8l-.3 1.6a1 1 0 0 1-1 .8h-1.4a1 1 0 0 1-1-.8l-.3-1.6c-.5-.2-1-.5-1.4-.8l-1.5.6a1 1 0 0 1-1.2-.4l-.7-1.2a1 1 0 0 1 .2-1.3l1.2-1a6.6 6.6 0 0 1 0-1.8l-1.2-1a1 1 0 0 1-.2-1.3l.7-1.2a1 1 0 0 1 1.2-.4l1.5.6c.4-.3.9-.6 1.4-.8Z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  )
}
function BridgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 48 32" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" d="M2 26h44" />
      <path strokeLinecap="round" d="M4 26V13a20 20 0 0 1 40 0v13" />
      <path strokeLinecap="round" d="M12 26v-8M20 26v-11M28 26v-11M36 26v-8" />
      <path strokeLinecap="round" d="M2 13h6M40 13h6" />
    </svg>
  )
}
function BulbIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 18h5M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .8 1.6v.5h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0 0 12 3Z" />
    </svg>
  )
}
function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m12 3 9 4.5-9 4.5-9-4.5L12 3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 12 8.5 4.3 8.5-4.3M3.5 16.5 12 20.8l8.5-4.3" />
    </svg>
  )
}
function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <path strokeLinecap="round" d="M4 20h16" />
      <path strokeLinecap="round" d="M7 20v-6M12 20V8M17 20v-9" />
    </svg>
  )
}
function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 20s-7.5-4.4-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6c0 5-7.5 9.4-7.5 9.4Z" />
    </svg>
  )
}
function ArrowRight({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5" />
    </svg>
  )
}
function Check({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.5 5 5 10-11" />
    </svg>
  )
}
function Cross({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  )
}

/* ====== Dados das seções ====== */

const MOVIMENTOS = [
  {
    key: 'clareza',
    kicker: 'Movimento 1',
    titulo: 'Clareza Estratégica',
    resumo: 'Organiza sua experiência, tese, direção e arquitetura do projeto.',
    Icone: TargetIcon,
    itens: [
      'Diagnóstico do momento atual',
      'Organização da experiência e da tese',
      'Definição da direção estratégica',
      'Arquitetura do projeto',
      'Plano inicial de ação',
    ],
  },
  {
    key: 'manifesto',
    kicker: 'Ponto de virada',
    titulo: 'Manifesto',
    resumo: 'Sua tese se torna um ativo concreto, com narrativa, posicionamento e livro-manifesto.',
    Icone: BookIcon,
    destaque: true,
    itens: [
      'Organização da tese',
      'Posicionamento e narrativa',
      'Criação do manifesto',
      'Livro como ativo central',
      'Clareza sobre a oferta e direção do negócio',
    ],
    rodape: 'O Manifesto transforma conhecimento em ativo.',
  },
  {
    key: 'implantacao',
    kicker: 'Movimento 3',
    titulo: 'Implantação',
    resumo: 'Do plano à realidade: seu projeto ganha operação, direção e acompanhamento.',
    Icone: GearIcon,
    itens: [
      'Implantação estratégica e tática',
      'Desenvolvimento da oferta',
      'Direção de posicionamento e execução',
      'Acompanhamento ao longo de 12 meses',
      'Evolução contínua do ecossistema',
    ],
  },
]

const SELOS = [
  { Icone: BulbIcon, label: 'Mais clareza' },
  { Icone: LayersIcon, label: 'Mais estrutura' },
  { Icone: ChartIcon, label: 'Mais impacto' },
  { Icone: HeartIcon, label: 'Mais legado' },
]

const PRESO_EM = ['na cabeça', 'na rotina', 'nas conversas', 'nos atendimentos', 'na própria presença']

const DESCOBERTAS = [
  'o que você realmente acredita',
  'qual problema você enxerga de maneira diferente',
  'como você toma decisões',
  'quais padrões existem na sua experiência',
  'qual transformação você consegue produzir',
  'qual é o seu método',
  'e por que alguém deveria procurar você',
]

const ENTREGAS = [
  {
    titulo: 'Diagnóstico estratégico',
    desc: 'Uma leitura estruturada do momento atual, experiência, mercado, oportunidades e direção.',
  },
  {
    titulo: 'Tese central',
    desc: 'Aquilo que você consegue defender e que passa a sustentar sua comunicação.',
  },
  {
    titulo: 'Posicionamento',
    desc: 'A maneira pela qual o mercado deverá perceber você, sua visão e seu trabalho.',
  },
  {
    titulo: 'Método',
    desc: 'A organização da forma como você pensa, decide e produz resultados.',
  },
  {
    titulo: 'Linguagem proprietária',
    desc: 'Conceitos, nomes, ideias e estruturas que ajudam o mercado a entender e lembrar sua proposta.',
  },
  {
    titulo: 'Livro-manifesto',
    desc: 'Sua tese transformada em uma obra real, organizada e estruturada.',
  },
  {
    titulo: 'Arquitetura de negócio',
    desc: 'Como essa propriedade intelectual pode se transformar em oferta, produtos, serviços e ativos.',
  },
  {
    titulo: 'Plano de implantação',
    desc: 'Uma visão clara do que construir, em qual ordem, com quais prioridades e qual movimento vem depois.',
  },
]

const NAO_E = [
  'Não é mais uma mentoria para ensinar você a criar um curso.',
  'Não queremos transformar você em uma cópia de outro expert.',
  'Não queremos encaixar sua experiência em uma metodologia pronta.',
  'Não queremos inventar três pilares bonitos e dizer que aquilo representa sua carreira.',
]

const PARA_QUEM = [
  'Você já acumulou experiência suficiente para saber que existe alguma coisa diferente na maneira como trabalha.',
  'É procurado por pessoas pedindo orientação, conselho ou ajuda.',
  'Possui uma ideia, método, conhecimento ou projeto que continua na gaveta.',
  'Quer construir uma nova fonte de receita sem necessariamente abandonar aquilo que já faz.',
  'Não quer começar o digital fingindo ser iniciante novamente.',
  'Quer transformar sua experiência em propriedade intelectual, posicionamento, livro, consultoria, programa, metodologia ou negócio.',
]

const NAO_E_PARA = [
  'uma promessa de dinheiro rápido',
  'um modelo pronto para copiar',
  'um curso com o nicho perfeito',
  'uma fórmula que funcione sem experiência',
  'um negócio construído sem participação sua',
]

const JORNADA = [
  {
    numero: '01',
    titulo: 'Primeiro entendemos',
    texto: 'Antes de produzir qualquer coisa, precisamos enxergar.',
    itens: ['sua história', 'experiência', 'casos', 'decisões', 'convicções', 'resultados', 'erros', 'padrões', 'mercado'],
  },
  {
    numero: '02',
    titulo: 'Depois extraímos',
    texto: 'Procuramos o que normalmente fica invisível para o próprio especialista. A maneira como você:',
    itens: ['pensa', 'diagnostica', 'decide', 'prioriza', 'resolve', 'ensina'],
  },
  {
    numero: '03',
    titulo: 'Depois organizamos',
    texto: 'Aquilo começa a ganhar:',
    itens: ['tese', 'método', 'linguagem', 'posicionamento', 'estrutura'],
  },
  {
    numero: '04',
    titulo: 'Depois materializamos',
    texto: 'O manifesto nasce. O que antes estava apenas na sua cabeça passa a existir em um ativo que pode:',
    itens: ['ser lido', 'entregue', 'citado', 'presenteado', 'usado numa reunião', 'levar você a um palco', 'gerar conversas', 'abrir oportunidades'],
  },
  {
    numero: '05',
    titulo: 'Depois decidimos o próximo movimento',
    texto: 'Você não precisa contratar a implantação. Pode levar tudo e executar sozinho. Mas se fizer sentido construir conosco, a próxima fase começa.',
    itens: [],
  },
]

const MANIFESTO_REUNE = [
  'clareza estratégica',
  'extração da tese',
  'organização do método',
  'posicionamento',
  'narrativa',
  'produção do livro-manifesto',
  'arquitetura da oferta',
  'plano para os próximos movimentos',
]

const IMPLANTACAO_TRABALHO = [
  'direção estratégica',
  'estruturação e evolução da oferta',
  'desenho dos produtos',
  'acompanhamento tático',
  'planejamento de aquisição',
  'posicionamento',
  'conteúdo estratégico',
  'comunidade',
  'indicadores',
  'análise',
  'decisão',
  'priorização',
  'revisão contínua do ecossistema',
]

const OPERACIONAL = ['design', 'tráfego', 'edição', 'programação', 'automação', 'social media', 'atendimento', 'produção audiovisual', 'tecnologia']

const CICLO = ['direção', 'execução', 'evidência', 'ajuste', 'próximo movimento']

const DELIBERADO = [
  'pequeno o suficiente para limitar risco',
  'concreto o suficiente para gerar alguma coisa real',
  'estratégico o suficiente para abrir o próximo passo',
  'relevante o suficiente para produzir evidência',
]

const POSSIBILIDADES = [
  'Seu livro?',
  'Seu método?',
  'Sua oferta?',
  'Seu posicionamento?',
  'Uma nova unidade de negócio?',
  'Uma segunda fonte de receita?',
  'Uma comunidade?',
  'Uma nova carreira?',
]

const FAQ_ITEMS = [
  {
    q: 'Preciso já saber exatamente qual produto quero criar?',
    a: 'Não. Na verdade, preferimos não começar pelo produto. O projeto começa investigando sua experiência, tese e oportunidade para então desenhar o que faz sentido construir.',
  },
  {
    q: 'Preciso já ter um livro escrito?',
    a: 'Não. A criação do livro-manifesto faz parte do projeto Manifesto.',
  },
  {
    q: 'Vocês apenas me ajudam a escrever ou produzem o livro?',
    a: 'O objetivo é produzir o livro como um ativo concreto, construído a partir da sua experiência, tese e materiais. Você participa como fonte intelectual central da obra.',
  },
  {
    q: 'O livro precisa falar diretamente sobre meu produto?',
    a: 'Não. Ele precisa sustentar sua tese. O livro deve funcionar como obra e como ativo intelectual, e não como catálogo comercial.',
  },
  {
    q: 'Posso contratar o Manifesto e depois executar sozinho?',
    a: 'Sim. Essa autonomia faz parte do modelo. Ao final você terá sua arquitetura e poderá construir com sua equipe ou fornecedores.',
  },
  {
    q: 'Sou obrigado a contratar os 12 meses de implantação?',
    a: 'Não. A implantação é uma decisão posterior. Você primeiro enxerga aquilo que foi estruturado e então decide qual será o próximo movimento.',
  },
  {
    q: 'A implantação inclui toda a execução operacional?',
    a: 'Não necessariamente. A eBuz atua principalmente na direção estratégica e tática. Demandas operacionais podem ser realizadas pela sua equipe ou por especialistas contratados conforme o projeto.',
  },
  {
    q: 'E se eu ainda não estiver preparado para o Manifesto completo?',
    a: 'Existe uma possibilidade de começar por uma etapa menor de clareza e arquitetura estratégica antes de avançar para a materialização completa. Essa alternativa é apresentada apenas quando fizer sentido para o momento do projeto.',
  },
]

interface HomeContentProps {
  logoUrl: string
  user?: { email: string; isAdmin: boolean; fullName?: string; phone?: string; document?: string; avatarUrl?: string; credits?: number } | null
  /** Isca do Jhow no topo (SDD-jhow-isca-fase1). Ausente: a home de sempre. */
  iscaNaHome?: boolean
}

export function HomeContent({ logoUrl, user, iscaNaHome = false }: HomeContentProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const router = useRouter()

  // Formulário de aplicação
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [ddi, setDdi] = useState('+55')
  const [telefone, setTelefone] = useState('')
  const [experiencia, setExperiencia] = useState('')
  const [momento, setMomento] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [formError, setFormError] = useState('')

  // Link legado: /?comprar=passaporte apontava pro checkout da oferta de entrada,
  // que saiu desta pagina. Manda pra rota dedicada COM o checkout ja aberto.
  // A rota chama /mapa-de-contexto, nao /passaporte: app/passaporte colidia com
  // app/app/passaporte (mesmo module id no bundle) e caia em /app/jornada.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('comprar') === 'passaporte') {
      router.replace('/mapa-de-contexto?comprar=1')
    }
  }, [router])

  function openWhatsApp() {
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Quero conhecer o projeto Manifesto da eBuz.')}`,
      '_blank'
    )
  }

  function irParaAplicacao() {
    setMenuOpen(false)
    document.getElementById('aplicacao')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  async function handleAplicacao(e: React.FormEvent) {
    e.preventDefault()
    if (!nome.trim() || !telefone.trim() || !experiencia.trim()) return
    setSending(true)
    setFormError('')
    try {
      const phoneClean = telefone.replace(/\D/g, '')
      const mensagem = [
        '[APLICAÇÃO MANIFESTO]',
        `Momento: ${momento || 'não informado'}`,
        `O que faz: ${experiencia.trim()}`,
      ].join('\n')

      const res = await fetch('/api/contato', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nome.trim(),
          email: email.trim(),
          phone: phoneClean,
          ddi,
          message: mensagem,
          source: 'site',
        }),
      })
      if (!res.ok) throw new Error('Falha')
      setSent(true)

      const ddiDigits = ddi.replace(/\D/g, '')
      fetch(FUNILCHATBOT_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nome.trim(),
          telefone: ddiDigits + phoneClean,
          mensagem: mensagem,
        }),
      }).catch(() => {})

      setNome('')
      setEmail('')
      setTelefone('')
      setExperiencia('')
      setMomento('')
    } catch {
      setFormError('Não foi possível enviar agora. Fale com a gente pelo WhatsApp.')
    } finally {
      setSending(false)
    }
  }

  const ctaLabel = 'Quero conhecer o projeto'

  return (
    <div className="min-h-screen bg-paper">
      {/* ========== HEADER ========== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-paper/90 backdrop-blur border-b border-ink-7">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Link href="/" className="shrink-0">
            <Image src={logoUrl} alt="eBuz" width={140} height={40} className="h-8 sm:h-9 w-auto" priority unoptimized={logoUrl.startsWith('http')} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV_LINKS.map((link) => (
              <a key={link.label} href={link.href} className="text-sm text-ink-3 hover:text-ink-0 transition-colors">
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <>
                {user.isAdmin && (
                  <Link href="/admin" className="hidden sm:inline text-sm text-ink-3 hover:text-ink-0 transition-colors">
                    Admin
                  </Link>
                )}
                <button
                  onClick={irParaAplicacao}
                  className="hidden sm:inline text-xs sm:text-sm bg-brand-grad text-white px-4 py-2 rounded-full shadow-glow hover:-translate-y-px transition font-semibold whitespace-nowrap"
                >
                  {ctaLabel}
                </button>
                <UserMenu
                  userName={user.fullName}
                  userEmail={user.email}
                  avatarUrl={user.avatarUrl}
                  isAdmin={user.isAdmin}
                  credits={user.credits}
                />
              </>
            ) : (
              <>
                <Link href="/login" className="hidden sm:inline text-sm text-ink-3 hover:text-ink-0 transition-colors">
                  Entrar
                </Link>
                <button
                  onClick={irParaAplicacao}
                  className="text-xs sm:text-sm bg-brand-grad text-white px-4 py-2 rounded-full shadow-glow hover:-translate-y-px transition font-semibold whitespace-nowrap shrink-0"
                >
                  {ctaLabel}
                </button>
              </>
            )}
            <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 text-ink-3" aria-label="Menu">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden bg-paper border-t border-ink-8 px-4 py-3 space-y-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2 text-sm text-ink-3 hover:bg-ink-9 rounded-lg"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-ink-7 mt-1 pt-2 space-y-2">
              {user ? (
                <Link href="/app" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-ink-0 hover:bg-ink-9 rounded-lg">
                  Meu painel
                </Link>
              ) : (
                <Link href="/login" onClick={() => setMenuOpen(false)} className="block px-3 py-2 text-sm text-ink-3 hover:bg-ink-9 rounded-lg">
                  Entrar
                </Link>
              )}
              <button
                onClick={irParaAplicacao}
                className="block w-full text-center px-3 py-2.5 text-sm font-semibold text-white bg-brand-grad rounded-full shadow-glow"
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ========== HERO ========== */}
      <section className="relative overflow-hidden bg-paper">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              'radial-gradient(60% 55% at 15% 0%, rgba(239,52,118,.10), transparent 70%), radial-gradient(55% 50% at 90% 10%, rgba(234,127,118,.12), transparent 70%)',
          }}
        />
        <div className="absolute inset-0 -z-10 grid-bg opacity-60" aria-hidden="true" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-28 sm:pt-36 lg:pt-40 pb-16 sm:pb-20 text-center">
          <p className="text-[11px] sm:text-xs font-semibold text-brand-500 uppercase tracking-[0.18em] mb-5">
            Ideias · Pessoas · Projetos que deixam legado
          </p>

          <h1 className="font-display text-[28px] leading-[1.15] sm:text-4xl lg:text-5xl font-extrabold text-ink-0 tracking-tight">
            O que você sabe já pode ser maior do que{' '}
            <span className="gradient-text">aquilo que você vende hoje</span>.
          </h1>

          <div className="mt-6 space-y-4 text-[15px] sm:text-lg text-ink-3 leading-relaxed max-w-2xl mx-auto">
            <p>Você acumulou experiência, decisões, histórias, repertório e uma maneira própria de resolver problemas.</p>
            <p>Talvez o que esteja faltando não seja aprender mais.</p>
            <p className="text-ink-1 font-medium">
              Talvez seja organizar o que já existe, transformar isso em uma tese clara e colocar essa tese no mundo como um ativo capaz de sustentar um novo negócio.
            </p>
          </div>

          <div className="mt-9 sm:mt-10">
            <p className="text-xs sm:text-sm font-semibold text-ink-4 uppercase tracking-wider mb-3">A eBuz transforma sua experiência em</p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {['clareza estratégica', 'manifesto', 'projeto implantado'].map((etapa, i) => (
                <div key={etapa} className="flex items-center gap-2 sm:gap-3">
                  <span
                    className={`px-3.5 py-2 rounded-full text-xs sm:text-sm font-bold border ${
                      i === 1
                        ? 'bg-brand-grad text-white border-transparent shadow-glow'
                        : 'bg-paper text-ink-1 border-ink-6'
                    }`}
                  >
                    {etapa}
                  </span>
                  {i < 2 && <ArrowRight className="w-4 h-4 text-brand-500 shrink-0" />}
                </div>
              ))}
            </div>
          </div>

          {iscaNaHome ? (
            <div className="mt-10 max-w-xl">
              <IscaChat />
            </div>
          ) : (
            <div className="mt-10">
              <button
                onClick={irParaAplicacao}
                className="w-full sm:w-auto bg-brand-grad text-white px-9 sm:px-12 py-4 rounded-full text-sm sm:text-base font-bold shadow-glow hover:-translate-y-px transition cursor-pointer"
              >
                {ctaLabel}
              </button>
              <p className="mt-3 text-xs text-ink-4">Conversa de avaliação, sem compromisso. Respondemos pelo WhatsApp.</p>
            </div>
          )}
        </div>
      </section>

      {/* ========== SEÇÃO 2: O PROBLEMA ========== */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-ink-9 border-y border-ink-7">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">O problema</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
            Tem gente com 15 anos de experiência ainda tentando descobrir o que vender.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-ink-3">E esse normalmente não é o verdadeiro problema.</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {['A pessoa sabe.', 'Ela atende.', 'Resolve.', 'Decide.'].map((t) => (
              <span key={t} className="px-3.5 py-1.5 rounded-full bg-paper border border-ink-7 text-sm font-medium text-ink-1">
                {t}
              </span>
            ))}
          </div>

          <p className="mt-6 text-base text-ink-3 leading-relaxed">
            Enxerga coisas que quem está começando ainda não consegue enxergar. Mas quase tudo isso continua preso:
          </p>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            {PRESO_EM.map((p) => (
              <div key={p} className="px-3 py-3 rounded-xl bg-paper border border-ink-7 text-center text-xs sm:text-sm text-ink-2 font-medium shadow-soft">
                {p}
              </div>
            ))}
          </div>

          <p className="mt-6 text-base text-ink-3 leading-relaxed">
            E enquanto esse conhecimento não é extraído, organizado e materializado, ele continua dependendo demais de você.
          </p>
          <p className="mt-4 text-base text-ink-3 leading-relaxed">
            É por isso que tanta gente experiente entra no digital e parece começar do zero. Tenta descobrir um curso. Inventa três pilares. Procura um nicho. Tenta encaixar 20 anos de carreira dentro de um template.
          </p>

          <div className="mt-10 rounded-2xl border border-ink-7 bg-paper p-6 sm:p-8 shadow-soft">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-ink-0 mb-6">Nós fazemos o caminho contrário.</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-ink-7 bg-ink-9 p-5">
                <p className="text-[11px] font-bold text-ink-4 uppercase tracking-wider mb-2">Não começamos perguntando</p>
                <p className="text-base sm:text-lg text-ink-4 line-through decoration-ink-6">Qual produto você quer vender?</p>
              </div>
              <div className="rounded-xl border-2 border-brand-500/30 p-5" style={{ background: 'rgba(239,52,118,.05)' }}>
                <p className="text-[11px] font-bold text-brand-500 uppercase tracking-wider mb-2">Começamos perguntando</p>
                <p className="text-base sm:text-lg font-bold text-ink-0">O que existe dentro daquilo que você já sabe?</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 3: A PROPOSTA (3 MOVIMENTOS) ========== */}
      <section id="proposta" className="relative py-16 sm:py-24 px-4 sm:px-6 bg-paper overflow-hidden scroll-mt-16">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage:
              'radial-gradient(45% 40% at 5% 15%, rgba(239,52,118,.07), transparent 70%), radial-gradient(45% 40% at 98% 85%, rgba(234,127,118,.08), transparent 70%)',
          }}
        />
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">A proposta</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
              Você não precisa inventar uma nova versão de si mesmo.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-3 leading-relaxed">
              Precisa encontrar a estrutura que já existe dentro da sua experiência. Da clareza estratégica à materialização do manifesto, e do manifesto à implantação do projeto.
            </p>
          </div>

          {/* Cards dos 3 movimentos */}
          <div className="grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-5 lg:gap-0 items-stretch">
            {MOVIMENTOS.map((mov, idx) => (
              <div key={mov.key} className="contents">
                <article
                  className={`relative rounded-3xl p-6 sm:p-7 flex flex-col ${
                    mov.destaque
                      ? 'border-2 border-brand-500/45 bg-paper shadow-glow lg:-mt-4 lg:mb-[-1rem] z-10'
                      : 'border border-ink-7 bg-ink-9/70 shadow-soft'
                  } ${idx > 0 ? 'lg:ml-5' : ''} ${idx < 2 ? 'lg:mr-5' : ''}`}
                  style={mov.destaque ? { background: 'linear-gradient(180deg, #fff 0%, rgba(255,241,245,.55) 100%)' } : undefined}
                >
                  {mov.destaque && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-brand-grad text-white text-[10px] font-bold uppercase tracking-[0.16em] px-4 py-1.5 rounded-full shadow-glow">
                      Ponto de virada
                    </span>
                  )}

                  <div className="flex flex-col items-center text-center">
                    <div
                      className={`w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full flex items-center justify-center mb-4 ${
                        mov.destaque ? 'bg-brand-grad' : ''
                      }`}
                      style={mov.destaque ? undefined : { background: 'rgba(239,52,118,.09)' }}
                    >
                      <mov.Icone className={`w-8 h-8 sm:w-9 sm:h-9 ${mov.destaque ? 'text-white' : 'text-brand-500'}`} />
                    </div>
                    <h3 className="font-display text-xl sm:text-2xl font-extrabold text-ink-0">{mov.titulo}</h3>
                    <p className="mt-2 text-sm text-ink-3 leading-relaxed">{mov.resumo}</p>
                  </div>

                  <div className="my-5 h-px bg-gradient-to-r from-transparent via-brand-500/35 to-transparent" />

                  <ul className="space-y-2.5 flex-1">
                    {mov.itens.map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(239,52,118,.1)' }}>
                          <Check className="w-3 h-3 text-brand-500" />
                        </span>
                        <span className="text-[13px] sm:text-sm text-ink-2 leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>

                  {mov.rodape && (
                    <p className="mt-5 rounded-xl px-4 py-3 text-center text-sm font-bold text-brand-500" style={{ background: 'rgba(239,52,118,.08)' }}>
                      {mov.rodape}
                    </p>
                  )}
                </article>

                {idx < 2 && (
                  <div className="flex lg:flex-col items-center justify-center py-1 lg:py-0">
                    <span className="w-10 h-10 rounded-full bg-brand-grad flex items-center justify-center shadow-glow rotate-90 lg:rotate-0">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Faixa da ponte */}
          <div className="mt-8 sm:mt-10 rounded-3xl bg-brand-grad px-6 sm:px-10 py-7 sm:py-8 shadow-glow">
            <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-8">
              <BridgeIcon className="w-16 h-11 sm:w-20 sm:h-14 text-white/90 shrink-0" />
              <div className="hidden sm:block w-px self-stretch bg-white/30" />
              <p className="text-center sm:text-left text-lg sm:text-2xl font-bold text-white leading-snug">
                O Manifesto é a ponte entre a clareza estratégica e a implantação do projeto no mundo real.
              </p>
            </div>
          </div>

          {/* Selos */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {SELOS.map((selo) => (
              <div key={selo.label} className="flex items-center justify-center gap-3 rounded-2xl border border-ink-7 bg-paper px-4 py-4 shadow-soft">
                <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(239,52,118,.1)' }}>
                  <selo.Icone className="w-5 h-5 text-brand-500" />
                </span>
                <span className="text-sm font-bold text-ink-1">{selo.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 4: FRASE DE VIRADA ========== */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 bg-ink-0 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.07]" aria-hidden="true" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[280px] rounded-full blur-[130px]" style={{ background: 'rgba(239,52,118,.28)' }} aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-snug tracking-tight">
            O Manifesto é a ponte entre aquilo que você sabe e{' '}
            <span className="gradient-text">aquilo que o mercado finalmente consegue enxergar</span>.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/70">Uma boa tese na sua cabeça ainda é invisível. Quando ela ganha:</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {['nome', 'linguagem', 'estrutura', 'argumentos', 'histórias', 'um livro', 'uma oferta'].map((t) => (
              <span key={t} className="px-3.5 py-1.5 rounded-full border border-white/20 bg-white/5 text-sm text-white/90">
                {t}
              </span>
            ))}
          </div>
          <p className="mt-6 text-base text-white/70">ela deixa de ser intenção.</p>
          <p className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white">Ela passa a existir.</p>
        </div>
      </section>

      {/* ========== SEÇÃO 5: POR QUE COMEÇAR PELO MANIFESTO ========== */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-paper">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">Por que começar pelo manifesto</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
            Não queremos simplesmente escrever um livro para você.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-ink-3 leading-relaxed">
            O livro é consequência de um processo maior. Para escrevê-lo, precisamos descobrir:
          </p>

          <ul className="mt-6 grid sm:grid-cols-2 gap-3">
            {DESCOBERTAS.map((d, i) => (
              <li key={d} className="flex items-start gap-3 rounded-xl border border-ink-7 bg-ink-9 px-4 py-3.5">
                <span className="text-xs font-black text-brand-500 mt-0.5 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-sm text-ink-2 leading-snug">{d}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 text-center rounded-3xl border border-ink-7 bg-ink-9 px-6 py-10">
            <p className="text-sm text-ink-3">É por isso que chamamos esse livro de</p>
            <p className="font-display text-4xl sm:text-6xl font-black tracking-tight gradient-text mt-2">MANIFESTO</p>
            <p className="mt-4 text-base text-ink-2 max-w-md mx-auto">
              Ele é o documento fundador daquilo que você pretende construir.
            </p>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 6: O QUE VOCÊ RECEBE ========== */}
      <section id="entrega" className="py-16 sm:py-20 px-4 sm:px-6 bg-ink-9 border-y border-ink-7 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">O que você recebe</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
              No projeto Manifesto, não entregamos apenas um arquivo em Word.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-ink-3">
              Nós construímos com você a base intelectual do seu próximo negócio. Ao final, você terá:
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {ENTREGAS.map((item, i) => (
              <article key={item.titulo} className="rounded-2xl border border-ink-7 bg-paper p-5 sm:p-6 shadow-soft hover:-translate-y-0.5 transition">
                <div className="flex items-start gap-4">
                  <span className="w-10 h-10 rounded-xl bg-brand-grad text-white flex items-center justify-center font-black text-sm shrink-0 shadow-glow">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-bold text-base text-ink-0">{item.titulo}</h3>
                    <p className="mt-1.5 text-sm text-ink-3 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 7: O QUE NÃO É ========== */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-ink-0">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-4">O que este projeto não é</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
            Não é mais uma mentoria para ensinar você a criar um curso.
          </h2>

          <ul className="mt-8 space-y-3">
            {NAO_E.slice(1).map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
                <Cross className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-white/80 leading-snug">{item}</span>
              </li>
            ))}
          </ul>

          <p className="mt-8 text-base text-white/70">E principalmente:</p>
          <p className="mt-2 font-display text-xl sm:text-2xl font-extrabold text-white">não queremos começar pelo produto.</p>

          <div className="mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
            {['Queremos começar pelo seu saber', 'Depois encontrar a tese', 'Depois estruturar o método', 'Depois materializar'].map((etapa, i) => (
              <div key={etapa} className="flex items-center gap-2 sm:gap-3">
                <span className="px-3.5 py-2 rounded-full border border-white/20 bg-white/5 text-xs sm:text-sm text-white/90">{etapa}</span>
                {i < 3 && <ArrowRight className="w-4 h-4 text-brand-400 shrink-0" />}
              </div>
            ))}
          </div>
          <p className="mt-6 text-base text-white/70">Só então desenhar o negócio que pode existir ao redor disso.</p>
        </div>
      </section>

      {/* ========== SEÇÃO 8 + 9: PARA QUEM É / NÃO É ========== */}
      <section id="para-quem" className="py-16 sm:py-20 px-4 sm:px-6 bg-paper scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">Para quem é</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
              Este projeto faz sentido para você se...
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* É pra você */}
            <div className="rounded-3xl border-2 border-brand-500/30 bg-paper p-6 sm:p-7 shadow-soft">
              <div className="flex items-center gap-3 pb-4 mb-5 border-b border-ink-7">
                <span className="w-9 h-9 rounded-full bg-brand-grad flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </span>
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-brand-500">É para você</h3>
              </div>
              <ul className="space-y-3.5">
                {PARA_QUEM.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-brand-500 shrink-0 mt-1" />
                    <span className="text-sm text-ink-2 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-5 border-t border-ink-7 text-sm font-bold text-ink-0">
                E principalmente: está cansado de continuar pensando no projeto sem colocar algo concreto no mundo.
              </p>
            </div>

            {/* Não é pra todo mundo */}
            <div className="rounded-3xl border border-ink-7 bg-ink-9 p-6 sm:p-7">
              <div className="flex items-center gap-3 pb-4 mb-5 border-b border-ink-7">
                <span className="w-9 h-9 rounded-full bg-ink-6/40 flex items-center justify-center shrink-0">
                  <Cross className="w-4 h-4 text-ink-3" />
                </span>
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-ink-3">Não é para todo mundo</h3>
              </div>
              <p className="text-sm text-ink-3 mb-4">Talvez este projeto não faça sentido se você procura:</p>
              <ul className="space-y-3.5">
                {NAO_E_PARA.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Cross className="w-4 h-4 text-ink-5 shrink-0 mt-0.5" />
                    <span className="text-sm text-ink-3 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-5 border-t border-ink-7 text-sm text-ink-2 leading-relaxed">
                Nós conseguimos ajudar a organizar e materializar aquilo que existe em você.{' '}
                <span className="font-bold text-ink-0">Não conseguimos fabricar vinte anos de experiência que ainda não existem.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 10: A JORNADA ========== */}
      <section id="jornada" className="py-16 sm:py-20 px-4 sm:px-6 bg-ink-9 border-y border-ink-7 scroll-mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">A jornada</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
              Do que você sabe até o que passa a existir.
            </h2>
          </div>

          <ol className="relative space-y-5 sm:space-y-6 sm:before:absolute sm:before:left-[27px] sm:before:top-4 sm:before:bottom-4 sm:before:w-px sm:before:bg-ink-7">
            {JORNADA.map((etapa) => (
              <li key={etapa.numero} className="relative flex flex-col sm:flex-row gap-4 sm:gap-6">
                <span className="relative z-10 w-14 h-14 rounded-2xl bg-brand-grad text-white flex items-center justify-center font-display font-black text-lg shrink-0 shadow-glow">
                  {etapa.numero}
                </span>
                <div className="flex-1 rounded-2xl border border-ink-7 bg-paper p-5 sm:p-6 shadow-soft">
                  <h3 className="font-display text-lg sm:text-xl font-bold text-ink-0">{etapa.titulo}</h3>
                  <p className="mt-2 text-sm text-ink-3 leading-relaxed">{etapa.texto}</p>
                  {etapa.itens.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {etapa.itens.map((it) => (
                        <span key={it} className="px-3 py-1.5 rounded-full bg-ink-9 border border-ink-7 text-xs text-ink-2">
                          {it}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ========== SEÇÃO 11: OFERTA PRINCIPAL ========== */}
      <section id="manifesto" className="py-16 sm:py-24 px-4 sm:px-6 bg-paper scroll-mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-[28px] border-2 border-brand-500/35 bg-paper overflow-hidden shadow-glow">
            <div className="bg-brand-grad px-6 sm:px-10 py-8 text-center">
              <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.2em]">O projeto</p>
              <h2 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight mt-1">MANIFESTO</h2>
              <p className="mt-3 text-sm sm:text-base text-white/90 max-w-lg mx-auto">
                Da experiência dispersa a uma tese que existe no mundo.
              </p>
            </div>

            <div className="p-6 sm:p-10">
              <p className="text-sm font-semibold text-ink-4 uppercase tracking-wider mb-4">O projeto Manifesto reúne</p>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {MANIFESTO_REUNE.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(239,52,118,.1)' }}>
                      <Check className="w-3 h-3 text-brand-500" />
                    </span>
                    <span className="text-sm text-ink-2">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 rounded-2xl bg-ink-9 border border-ink-7 p-6">
                <p className="text-sm text-ink-3">Você termina com algo muito diferente de mais conhecimento. Você termina com:</p>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {['uma tese clara', 'um livro real', 'um negócio possível desenhado', 'um próximo movimento definido'].map((item) => (
                    <div key={item} className="rounded-xl bg-paper border border-ink-7 px-4 py-4 text-center text-sm font-bold text-ink-0 shadow-soft">
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={irParaAplicacao}
                  className="w-full sm:w-auto bg-brand-grad text-white px-10 py-4 rounded-full text-sm sm:text-base font-bold shadow-glow hover:-translate-y-px transition cursor-pointer"
                >
                  Quero criar meu Manifesto
                </button>
                <p className="mt-3 text-xs text-ink-4">
                  Cada projeto é desenhado a partir do seu momento. O escopo e o investimento são apresentados na conversa de avaliação.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 12 + 13 + 14: IMPLANTAÇÃO ========== */}
      <section id="implantacao" className="py-16 sm:py-24 px-4 sm:px-6 bg-ink-0 scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-4">E se você não quiser parar no plano?</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-white tracking-tight">IMPLANTAÇÃO</h2>
            <p className="mt-4 text-base sm:text-lg text-white/75 leading-relaxed">
              12 meses para transformar a arquitetura em um negócio operando. Depois de organizar o saber e criar o manifesto, podemos permanecer ao seu lado na implantação.
            </p>
          </div>

          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
            <p className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-5">Nosso trabalho passa a ser</p>
            <div className="flex flex-wrap gap-2">
              {IMPLANTACAO_TRABALHO.map((item) => (
                <span key={item} className="px-3.5 py-2 rounded-full border border-white/15 bg-white/5 text-xs sm:text-sm text-white/85">
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* O operacional */}
          <div className="mt-6 grid lg:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
              <h3 className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">
                A eBuz não precisa virar sua agência para implantar seu negócio.
              </h3>
              <p className="mt-4 text-sm text-white/70 leading-relaxed">Existe uma diferença importante entre direção e execução operacional. Na implantação, permanecemos principalmente nos níveis:</p>
              <div className="mt-5 flex items-center gap-3">
                <span className="flex-1 text-center rounded-xl bg-brand-grad text-white px-4 py-3 text-sm font-extrabold uppercase tracking-wider shadow-glow">Estratégico</span>
                <span className="text-white/40 font-bold">+</span>
                <span className="flex-1 text-center rounded-xl bg-brand-grad text-white px-4 py-3 text-sm font-extrabold uppercase tracking-wider shadow-glow">Tático</span>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
              <p className="text-sm text-white/70 leading-relaxed">
                O operacional pode ser executado pela sua equipe ou por profissionais contratados conforme a necessidade:
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {OPERACIONAL.map((item) => (
                  <span key={item} className="px-3 py-1.5 rounded-full border border-white/15 text-xs text-white/75">
                    {item}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-sm text-white/85 font-medium leading-relaxed">
                A eBuz ajuda você a garantir que todas essas peças estejam construindo o mesmo projeto.
              </p>
            </div>
          </div>

          {/* Por que 12 meses */}
          <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">
              Por que 12 meses? Porque um negócio não termina quando a ideia fica bonita no papel.
            </h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {['construir', 'colocar no mercado', 'observar', 'medir', 'corrigir', 'validar', 'refinar', 'crescer'].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full border border-white/15 text-xs text-white/75">
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm text-white/70 leading-relaxed">
              O que funciona no mês dois provavelmente será diferente no mês oito. Por isso a implantação não é uma sequência de aulas. É um ciclo contínuo de:
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
              {CICLO.map((etapa, i) => (
                <div key={etapa} className="flex items-center gap-2 sm:gap-3">
                  <span className="px-3.5 py-2 rounded-full bg-brand-grad text-white text-xs sm:text-sm font-bold shadow-glow">{etapa}</span>
                  {i < CICLO.length - 1 && <ArrowRight className="w-4 h-4 text-brand-400 shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 15 + 16: MOVIMENTO DELIBERADO + CUSTO DA ESPERA ========== */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-paper">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6">
          {/* Movimento deliberado */}
          <div className="rounded-3xl border border-ink-7 bg-ink-9 p-6 sm:p-8">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">Movimento deliberado</p>
            <h3 className="font-display text-xl sm:text-2xl font-extrabold text-ink-0 leading-snug">
              Não acreditamos em saltos de fé. Acreditamos em movimentos deliberados.
            </h3>
            <ul className="mt-5 space-y-3">
              {DELIBERADO.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-xl bg-paper border border-ink-7 px-4 py-3">
                  <Check className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
                  <span className="text-sm text-ink-2 leading-snug">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-ink-3 leading-relaxed">
              Você não precisa decidir hoje como serão os próximos dez anos.{' '}
              <span className="font-bold text-ink-0">Precisa decidir qual movimento merece existir agora.</span>
            </p>
          </div>

          {/* Custo da espera */}
          <div className="rounded-3xl border-2 border-brand-500/25 bg-paper p-6 sm:p-8 shadow-soft">
            <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">O custo da espera</p>
            <h3 className="font-display text-xl sm:text-2xl font-extrabold text-ink-0 leading-snug">
              Seu projeto está realmente maturando? Ou só está ficando mais velho?
            </h3>
            <p className="mt-4 text-sm text-ink-3 leading-relaxed">Queijo matura porque existe processo. Existe:</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {['temperatura', 'condição', 'controle', 'intervenção', 'tempo'].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-ink-9 border border-ink-7 text-xs text-ink-2">
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-5 text-sm text-ink-3 leading-relaxed">
              Uma ideia esquecida não melhora simplesmente porque passaram mais três anos.
            </p>
            <div className="mt-5 rounded-2xl px-5 py-5 text-center" style={{ background: 'rgba(239,52,118,.07)' }}>
              <p className="text-base font-bold text-ink-4 line-through decoration-brand-500/50">Tempo não amadurece projetos.</p>
              <p className="mt-1 font-display text-2xl font-black gradient-text">Processo amadurece.</p>
            </div>
            <p className="mt-5 text-sm text-ink-3 leading-relaxed">
              Talvez você não precise de mais tempo. Talvez precise colocar seu projeto diante da realidade.
            </p>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 17: A GRANDE PERGUNTA ========== */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 bg-ink-9 border-y border-ink-7 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">A grande pergunta</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-ink-0 tracking-tight leading-tight">
            O que pode existir daqui a 12 meses que hoje só existe na sua cabeça?
          </h2>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {POSSIBILIDADES.map((p) => (
              <div key={p} className="rounded-2xl border border-ink-7 bg-paper px-4 py-5 text-sm font-semibold text-ink-1 shadow-soft">
                {p}
              </div>
            ))}
          </div>

          <p className="mt-8 text-base text-ink-3">Talvez você ainda não saiba a resposta inteira. Tudo bem.</p>
          <p className="mt-2 text-lg sm:text-xl font-bold text-ink-0">Nosso trabalho é ajudar você a descobrir o primeiro movimento.</p>

          <button
            onClick={irParaAplicacao}
            className="mt-8 w-full sm:w-auto bg-brand-grad text-white px-10 py-4 rounded-full text-sm sm:text-base font-bold shadow-glow hover:-translate-y-px transition cursor-pointer"
          >
            Quero começar
          </button>
        </div>
      </section>

      {/* ========== SEÇÃO 18: SOBRE A EBUZ ========== */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-paper">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
            <div>
              <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">Sobre a eBuz</p>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-ink-0 tracking-tight leading-tight">
                Pessoas. Ideias. Projetos que deixam legado.
              </h2>
            </div>
            <div className="space-y-4 text-base text-ink-3 leading-relaxed">
              <p>A eBuz existe para transformar conhecimento em ação.</p>
              <p>Não acreditamos que toda pessoa experiente precise virar influenciador. Nem que toda experiência deva virar um curso.</p>
              <p>Acreditamos que boas ideias precisam encontrar:</p>
              <div className="flex flex-wrap gap-2">
                {['estrutura', 'linguagem', 'forma', 'distribuição', 'mercado'].map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-ink-9 border border-ink-7 text-xs font-medium text-ink-2">
                    {t}
                  </span>
                ))}
              </div>
              <p className="text-ink-1 font-medium">Nosso trabalho começa no saber. E termina quando aquilo ganha vida no mundo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO 19: FAQ ========== */}
      <section id="faq" className="py-16 sm:py-20 px-4 sm:px-6 bg-ink-9 border-y border-ink-7 scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs font-semibold text-brand-500 uppercase tracking-wider mb-4">Dúvidas</p>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-ink-0 tracking-tight mb-8">Perguntas frequentes</h2>

          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div key={item.q} className="rounded-2xl border border-ink-7 bg-paper overflow-hidden shadow-soft">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 sm:px-6 py-4 flex items-center justify-between gap-4 text-left cursor-pointer"
                  aria-expanded={openFaq === i}
                >
                  <span className="text-sm sm:text-base font-semibold text-ink-0">{item.q}</span>
                  <svg
                    className={`w-4 h-4 text-ink-4 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && <div className="px-5 sm:px-6 pb-5 text-sm text-ink-3 leading-relaxed">{item.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO FINAL + APLICAÇÃO ========== */}
      <section id="aplicacao" className="relative py-16 sm:py-24 px-4 sm:px-6 bg-ink-0 overflow-hidden scroll-mt-16">
        <div className="absolute inset-0 grid-bg opacity-[0.07]" aria-hidden="true" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[560px] h-[300px] rounded-full blur-[130px]" style={{ background: 'rgba(239,52,118,.25)' }} aria-hidden="true" />

        <div className="relative max-w-5xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Texto final */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Existe uma diferença entre ter uma boa ideia e ter alguma coisa no mundo.
            </h2>
            <ul className="mt-6 space-y-2.5">
              {['Uma ideia não pode ser lida.', 'Não pode ser testada.', 'Não pode ser criticada.', 'Não pode gerar uma conversa.', 'Não pode construir reputação.', 'Não pode abrir uma porta.'].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm sm:text-base text-white/70">
                  <Cross className="w-4 h-4 text-brand-400 shrink-0 mt-1" />
                  {t}
                </li>
              ))}
            </ul>
            <p className="mt-7 text-base text-white/80">Quando sua experiência ganha forma, alguma coisa muda. Você deixa de dizer:</p>
            <p className="mt-3 text-lg text-white/50 line-through decoration-white/30">Um dia eu vou fazer.</p>
            <p className="mt-2 font-display text-3xl sm:text-4xl font-black gradient-text">Isso existe.</p>
          </div>

          {/* Formulário */}
          <div className="rounded-3xl bg-paper p-6 sm:p-8 shadow-2xl">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-brand-grad flex items-center justify-center mx-auto mb-4 shadow-glow">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-ink-0">Recebemos sua aplicação.</h3>
                <p className="mt-2 text-sm text-ink-3 leading-relaxed">
                  Vamos ler o que você escreveu e entrar em contato pelo WhatsApp para marcar a conversa de avaliação.
                </p>
                <button onClick={openWhatsApp} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition">
                  {WA_ICON}
                  Falar agora no WhatsApp
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-xl sm:text-2xl font-extrabold text-ink-0 leading-snug">
                  Se você acredita que chegou a hora de descobrir o que pode existir a partir daquilo que você já sabe:
                </h3>
                <p className="mt-3 text-sm text-ink-3">
                  Conte rapidamente o seu momento. A conversa de avaliação é individual e serve para os dois lados decidirem se faz sentido.
                </p>

                <form onSubmit={handleAplicacao} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="ap-nome" className="block text-xs font-semibold text-ink-3 mb-1.5">Nome *</label>
                    <input
                      id="ap-nome"
                      type="text"
                      required
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      className="w-full px-4 py-3 border border-ink-7 rounded-xl text-sm text-ink-0 focus:outline-none focus:border-brand-500 transition-colors"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  <div>
                    <label htmlFor="ap-tel" className="block text-xs font-semibold text-ink-3 mb-1.5">WhatsApp *</label>
                    <div className="flex gap-2">
                      <select
                        value={ddi}
                        onChange={(e) => setDdi(e.target.value)}
                        aria-label="Código do país"
                        className="w-24 shrink-0 px-2 py-3 border border-ink-7 rounded-xl text-xs text-ink-1 bg-paper focus:outline-none focus:border-brand-500"
                      >
                        {DDI_OPTIONS.map((opt) => (
                          <option key={opt.code} value={opt.code}>{opt.label}</option>
                        ))}
                      </select>
                      <input
                        id="ap-tel"
                        type="tel"
                        required
                        value={telefone}
                        onChange={(e) => setTelefone(e.target.value)}
                        className="flex-1 px-4 py-3 border border-ink-7 rounded-xl text-sm text-ink-0 focus:outline-none focus:border-brand-500 transition-colors"
                        placeholder="(61) 99999-9999"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="ap-email" className="block text-xs font-semibold text-ink-3 mb-1.5">E-mail</label>
                    <input
                      id="ap-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-ink-7 rounded-xl text-sm text-ink-0 focus:outline-none focus:border-brand-500 transition-colors"
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="ap-exp" className="block text-xs font-semibold text-ink-3 mb-1.5">
                      Em uma frase: o que as pessoas procuram você para resolver? *
                    </label>
                    <textarea
                      id="ap-exp"
                      required
                      rows={3}
                      value={experiencia}
                      onChange={(e) => setExperiencia(e.target.value)}
                      className="w-full px-4 py-3 border border-ink-7 rounded-xl text-sm text-ink-0 focus:outline-none focus:border-brand-500 transition-colors resize-none"
                      placeholder="Ex.: sou advogado tributarista há 18 anos e resolvo passivo fiscal de empresa familiar."
                    />
                  </div>

                  <div>
                    <label htmlFor="ap-momento" className="block text-xs font-semibold text-ink-3 mb-1.5">Seu momento hoje</label>
                    <select
                      id="ap-momento"
                      value={momento}
                      onChange={(e) => setMomento(e.target.value)}
                      className="w-full px-4 py-3 border border-ink-7 rounded-xl text-sm text-ink-1 bg-paper focus:outline-none focus:border-brand-500"
                    >
                      <option value="">Selecione</option>
                      {MOMENTOS.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  {formError && <p className="text-xs text-red-600 bg-red-50 px-3 py-2.5 rounded-lg">{formError}</p>}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-brand-grad text-white py-4 rounded-full text-sm sm:text-base font-bold shadow-glow hover:-translate-y-px transition disabled:opacity-60 disabled:hover:translate-y-0 cursor-pointer"
                  >
                    {sending ? 'Enviando...' : 'Quero conhecer o projeto'}
                  </button>
                  <p className="text-[11px] text-ink-4 text-center leading-relaxed">
                    Seus dados são usados apenas para esse contato. Sem disparo em massa.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>

      </section>

      {/* ========== BOTÃO FLUTUANTE WHATSAPP ========== */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center group cursor-pointer"
        aria-label="Falar no WhatsApp"
      >
        {WA_ICON}
        <span className="absolute right-full mr-3 bg-ink-0 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Fale conosco
        </span>
      </button>

      {/* ========== FOOTER ========== */}
      <SiteFooter />
    </div>
  )
}
